import {
  enableProdMode,
  APP_INITIALIZER,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withHashLocation, Routes } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LocationStrategy, HashLocationStrategy, DatePipe } from '@angular/common';
import { provideNativeDateAdapter } from '@angular/material/core';

import {
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
} from '@azure/msal-browser';

import { AppComponent } from './app/app.component';
import { environment, adminConfig } from './environments/environment';
import { initWithLaunchDarkly } from './app/core/launchDarkly/launchDarkly.factory';
import { LaunchDarklyService } from './app/core/launchDarkly/launchDarkly.service';
import { SpinnerInterceptor } from './app/interceptors/spinner.interceptor';
import { ErrorHandlerService } from './app/services/error-handler.service';
import { ToastrModule } from 'ngx-toastr';

// Import components for routes
import { MainLayoutComponent } from './app/_layouts/components/main-layout/main-layout.component';
import { WalkpathOrderComponent } from './app/_layouts/pages/walkpath-order/walkpath-order.component';
import { HandoffSettingsComponent } from './app/_layouts/pages/handoff-settings/handoff-settings.component';
import { ManageItemsComponent } from './app/_layouts/pages/manage-items/manage-items.component';
import { ManageModifiersComponent } from './app/_layouts/pages/manage-modifiers/manage-modifiers.component';
import { PrepLocationsComponent } from './app/_layouts/pages/prep-locations/prep-locations.component';
import { LoginFailedComponent } from './app/_layouts/pages/login-failed/login-failed.component';
import { StoreGuard } from './app/guards/store.guard';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

// MSAL Configuration Factories
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: adminConfig.clientId,
      authority: adminConfig.authority + '/' + adminConfig.tenant,
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(adminConfig.baseAdminApiUri + '/*', adminConfig.adminApiScope);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// Application Routes
const routes: Routes = [
  {
    path: '',
    component: MainLayoutComponent,
    canActivateChild: [StoreGuard],
    children: [
      {
        path: 'store-settings',
        loadChildren: () =>
          import('./app/modules/store-settings/store-settings-routing.module').then((m) => m.STORE_SETTINGS_ROUTES),
      },
      {
        path: 'walkpath-order',
        component: WalkpathOrderComponent,
        canActivate: [MsalGuard],
      },
      {
        path: 'handoff-settings',
        component: HandoffSettingsComponent,
        canActivate: [MsalGuard],
      },
      {
        path: 'manage-items',
        component: ManageItemsComponent,
        canActivate: [MsalGuard],
      },
      {
        path: 'prep-locations',
        component: PrepLocationsComponent,
        canActivate: [MsalGuard],
      },
      {
        path: 'manage-modifiers',
        component: ManageModifiersComponent,
        canActivate: [MsalGuard],
      },
    ],
  },
  {
    // Needed for hash routing
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/070f3435954cb264a55b012adae788f8eab8d46b/samples/msal-angular-v2-samples/angular11-sample-app/src/app/app-routing.module.ts#L32
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/3292
    path: 'code',
    component: MainLayoutComponent,
  },
  {
    path: 'login-failed',
    component: LoginFailedComponent,
  },
];

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    // Router configuration with hash location strategy
    provideZoneChangeDetection(),
    provideRouter(routes, withHashLocation()),

    // Turn off popover so that z-index will work
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },

    // HTTP Client with interceptors
    provideHttpClient(withInterceptorsFromDi()),

    // Animations
    provideAnimations(),

    // Import providers from NgModule-based libraries
    importProvidersFrom(ToastrModule.forRoot()),

    // Common providers
    DatePipe,
    provideNativeDateAdapter(),

    // LaunchDarkly initialization
    {
      provide: APP_INITIALIZER,
      useFactory: initWithLaunchDarkly,
      deps: [LaunchDarklyService],
      multi: true,
    },

    // MSAL Authentication Configuration
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },

    // Global Error Handler
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },

    // Location Strategy (Hash-based routing)
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
  ],
}).catch((err) => console.error(err));
